import loadable from '@loadable/component'
import React, { useState, useCallback } from 'react'

import Gap from '@/common/components/Gap'
import Separator from '@/common/components/Separator'
import ArrowDownIcon from '@/common/components/icons/ArrowDownIcon'
import ArrowUpIcon from '@/common/components/icons/ArrowUpIcon'
import { BEDROOM_OPTIONS } from '@/common/enums'
import useI18n from '@/common/hooks/useI18n'
import useSwitch from '@/common/hooks/useSwitch'
import { SEE_MORE, SEE_LESS, MAPS } from '@/common/translation'
import { PrefetchRequest } from '@/common/types'
import { numberToString } from '@/common/utils/string'
import { getUrlParams } from '@/common/utils/url'
import {
  SIZE_MIN,
  SIZE_MAX,
  PRICE_MIN,
  PRICE_MAX,
} from '@/modules/property/components/UnitListTable/constants'
import usePlaceRooms, {
  prefetchPlaceRooms,
} from '@/modules/property/hooks/usePlaceRooms'
import {
  OVERVIEW,
  ALL_UNITS,
  AVERAGE_PRICE,
} from '@/modules/property/pages/PlaceDetailPage/translation'
import { PLACE_DETAIL_PATH } from '@/modules/property/paths'
import { N_ROOMS } from '@/modules/property/translation'

import { AveragePriceWrapper } from './styled'

const UnitListTable = loadable(
  () => import('@/modules/property/components/UnitListTable')
)
const NearbyPlaces = loadable(() => import('./components/NearbyPlaces'))

const DEFAULT_FILTER = {
  status: 'available',
  bedroom: undefined,
  size: [SIZE_MIN, SIZE_MAX] as [number, number],
  floor: undefined,
  price: [PRICE_MIN, PRICE_MAX] as [number, number],
  furniture: undefined,
}
const MAX_OVERVIEW_LENGTH = 500

interface DesktopPlaceDetailProps {
  place: RentAPerfect.Condo
}

const DesktopPlaceDetail = ({ place }: DesktopPlaceDetailProps) => {
  const { t } = useI18n()
  const [filter, setFilter] = useState(DEFAULT_FILTER)
  const { data: rooms, isLoading: isLoadingRooms } = usePlaceRooms({
    ...filter,
    placeId: place.id,
  })
  const { value: isShowFullOverview, toggle: toggleOverview } = useSwitch(
    (place as any).description?.length <= MAX_OVERVIEW_LENGTH
  )

  const showingOverview = isShowFullOverview
    ? (place as any).description
    : `${(place as any).description?.slice(0, 500)}...`

  const onFilterChange = useCallback(
    (key: keyof typeof DEFAULT_FILTER) => value => {
      if (value === filter[key]) return
      setFilter({ ...filter, [key]: value })
    },
    [filter]
  )

  return (
    <Gap space={40} vertical>
      {(place as any).description ? (
        <div>
          <h2 className='text-24 text-bold'>{t(OVERVIEW)}</h2>
          <Separator className='my-16' />
          <div className='text-14 text-medium'>{showingOverview}</div>
          <Gap
            className='text-primary align-center clickable width-fit-content my-16'
            onClick={toggleOverview}
            space={4}
          >
            <div className='text-14 text-semibold'>
              {isShowFullOverview ? t(SEE_LESS) : t(SEE_MORE)}
            </div>
            {isShowFullOverview ? (
              <ArrowUpIcon size={10} />
            ) : (
              <ArrowDownIcon size={10} />
            )}
          </Gap>
        </div>
      ) : null}
      <div>
        <Gap className='align-center' space={12}>
          <h2 className='text-24 text-bold'>{t(ALL_UNITS)}</h2>
          <div className='text-gray text-16 text-medium'>
            ({t(N_ROOMS, { n: rooms.length })})
          </div>
        </Gap>
        <Separator className='my-16' />
        <UnitListTable
          isLoading={isLoadingRooms}
          data={rooms}
          filter={filter}
          onFilterChange={onFilterChange}
        />
      </div>
      {place.avgPrice && !Array.isArray(place.avgPrice) ? (
        <div>
          <h2 className='text-24 text-bold'>{t(AVERAGE_PRICE)}</h2>
          <Separator className='my-16' />
          <AveragePriceWrapper>
            {BEDROOM_OPTIONS.slice(0, 4).map(({ value, label }) => (
              <div key={`average-price-${value}`}>
                <div className='text-14 text-medium mb-2'>{t(label)}</div>
                <div className='text-16 text-semibold'>
                  {place.avgPrice?.[`b${value}`]
                    ? `฿${numberToString(place.avgPrice[`b${value}`])}`
                    : '-'}
                </div>
              </div>
            ))}
          </AveragePriceWrapper>
        </div>
      ) : null}
      {place.NearbyPlace?.length ? (
        <div>
          <h2 className='text-24 text-bold'>{t(MAPS)}</h2>
          <Separator className='my-16' />
          <NearbyPlaces place={place} />
        </div>
      ) : null}
    </Gap>
  )
}

DesktopPlaceDetail.prefetch = async (request: PrefetchRequest) => {
  const { placeId } = getUrlParams(PLACE_DETAIL_PATH, request.url) as {
    placeId: string
  }

  await Promise.all([
    prefetchPlaceRooms(request, { ...DEFAULT_FILTER, placeId }),
  ])
}

export default DesktopPlaceDetail
